import { useEffect, useState } from "react";
import { api, errText } from "../../lib/api";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "../ui/dialog";
import { toast } from "sonner";
import { Trash2, Plus } from "lucide-react";

export const AssignRulesManager = ({ open, onOpenChange, team }) => {
  const [rules, setRules] = useState([]);
  const [keyword, setKeyword] = useState("");
  const [assignee, setAssignee] = useState("");
  const load = () => api.get("/admin/assign-rules").then((r) => setRules(r.data)).catch(() => {});
  useEffect(() => { if (open) load(); }, [open]);
  const add = async () => {
    if (!keyword.trim() || !assignee) return toast.error("Enter a keyword and pick a staff member");
    try { await api.post("/admin/assign-rules", { keyword: keyword.trim(), assignee_id: assignee }); setKeyword(""); setAssignee(""); toast.success("Rule added"); load(); }
    catch (e) { toast.error(errText(e)); }
  };
  const remove = async (id) => {
    try { await api.delete(`/admin/assign-rules/${id}`); toast.success("Rule removed"); load(); }
    catch (e) { toast.error(errText(e)); }
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-xl" data-testid="assign-rules-manager">
        <DialogHeader>
          <DialogTitle>Auto-assign rules</DialogTitle>
          <DialogDescription>New contact messages whose subject or body contains a keyword are automatically assigned to a staff member. First matching rule wins.</DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          {rules.length === 0 && <p className="text-sm text-slate-400">No rules yet — add one below.</p>}
          {rules.map((r) => (
            <div key={r.id} className="rounded-xl border border-slate-200 p-3 flex items-center justify-between gap-3" data-testid={`assign-rule-${r.id}`}>
              <p className="text-sm text-slate-700"><span className="font-mono bg-slate-100 rounded px-1.5 py-0.5 text-[13px]">{r.keyword}</span> <span className="text-slate-400">→</span> <span className="font-semibold text-[#12234A]">{r.assignee_name}</span></p>
              <Button size="sm" variant="ghost" onClick={() => remove(r.id)} data-testid={`assign-rule-delete-${r.id}`} className="rounded-lg h-8 text-red-500 hover:text-red-600"><Trash2 size={14} /></Button>
            </div>
          ))}
          <div className="rounded-xl border border-dashed border-slate-300 p-3" data-testid="assign-rule-add">
            <p className="text-xs font-semibold text-slate-500 mb-2">Add a rule</p>
            <div className="flex items-center gap-2">
              <Input value={keyword} onChange={(e) => setKeyword(e.target.value)} placeholder="Keyword (e.g. advertising)" data-testid="assign-rule-keyword" className="rounded-lg h-9" maxLength={60} />
              <Select value={assignee} onValueChange={setAssignee}>
                <SelectTrigger data-testid="assign-rule-assignee" className="h-9 rounded-lg w-[180px]"><SelectValue placeholder="Assign to…" /></SelectTrigger>
                <SelectContent>
                  {team.map((t) => (<SelectItem key={t.user_id} value={t.user_id} data-testid={`assign-rule-opt-${t.user_id}`}>{t.name}</SelectItem>))}
                </SelectContent>
              </Select>
            </div>
            <Button size="sm" onClick={add} data-testid="assign-rule-save" className="rounded-lg h-8 mt-2 gap-1"><Plus size={14} /> Add rule</Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};
